import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, User } from 'lucide-react';
import Layout from '../Layout';
import { createUser, credentialCheck, loginUser, updateUser } from '../../helpers/user';
import { showToast } from '../../helpers/customToast';

const AuthPage = () => {
    const [isLogin, setIsLogin] = useState(true);
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const redirectTo = queryParams.get('redirect');
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        email: '',
        password: '',
        confirmPassword: '',
        first_name: '',
        last_name: '',
    });

    const [formErrors, setFormErrors] = useState({
        email: '',
        password: '',
        confirmPassword: '',
        first_name: '',
        last_name: '',
    });

    const validateEmail = (email) => {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(email);
    };

    const validateForm = () => {
        const errors = {};
        let isValid = true;

        if (!formData.email) {
            errors.email = 'Email is required';
            isValid = false;
        } else if (!validateEmail(formData.email)) {
            errors.email = 'Invalid email format';
            isValid = false;
        }

        if (!formData.password) {
            errors.password = 'Password is required';
            isValid = false;
        } else if (formData.password.length < 8) {
            errors.password = 'Password must be at least 8 characters';
            isValid = false;
        }

        if (!isLogin) {
            if (!formData.first_name) {
                errors.first_name = 'First name is required';
                isValid = false;
            }

            if (!formData.last_name) {
                errors.last_name = 'Last name is required';
                isValid = false;
            }

            if (formData.password !== formData.confirmPassword) {
                errors.confirmPassword = 'Passwords do not match';
                isValid = false;
            }
        }

        setFormErrors(errors);
        return isValid;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when user starts typing
        setFormErrors(prev => ({
            ...prev,
            [name]: ''
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // if (!validateForm()) {
        //   return;
        // }

        setLoading(true);
        try {
            if(isLogin){
                const response = await credentialCheck({...formData, identifier: formData.email});
                if(response){
                    loginUser(response);
                    showToast('Bienvenue !', 'success');
                    if (redirectTo === 'checkout') {
                        navigate('/checkout');
                    }else{
                        navigate('/mon-compte');
                    }
                    return;
                }else{
                    setError('Une erreur est survenue, merci de réessayer.', 'error');
                    return;
                }
            }
            let register;
            try{
                register = await createUser({username: formData.email, email: formData.email, password: formData.password});
                // console.log(register.user.documentId);
            }catch(e){
                console.error(e);
                setError('Une erreur est survenue, merci de réessayer.', 'error');
            }finally{
                await updateUser(register.user.id, {first_name: formData.first_name, last_name: formData.last_name});
                loginUser(register);
                showToast('Bienvenue !', 'success');
                if (redirectTo === 'checkout') {
                    navigate('/checkout');
                }else{
                    navigate('/mon-compte');
                }
            }
        } catch (error) {
            console.error(error)
            setError(error.response?.data?.error.message || 'An error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Layout>
            <div className="container my-5">
                <div className="row justify-content-center">
                    <div className="col-md-6 col-lg-6">
                        <div className="card shadow-sm">
                            <div className="card-body p-4">
                                <div className="text-center mb-4">
                                    <h2 className="mb-1">{isLogin ? 'Bienvenue !' : 'Créer un compte'}</h2>
                                    <p className="text-muted">
                                        {isLogin
                                            ? 'Connectez-vous afin de continuer'
                                            : 'Remplissez le formulaire afin de vous inscrire'}
                                    </p>
                                </div>

                                {error && (
                                    <div className="alert alert-danger" role="alert">
                                        {error}
                                    </div>
                                )}

                                <form onSubmit={handleSubmit}>
                                    {!isLogin && (
                                        <div className="row">
                                            <div className="col-md-6 mb-3">
                                                <label className="form-label">Prénom</label>
                                                <div className="input-group">
                                                    <span className="input-group-text">
                                                        <User size={18} />
                                                    </span>
                                                    <input
                                                        type="text"
                                                        className={`form-control ${formErrors.first_name ? 'is-invalid' : ''}`}
                                                        name="first_name"
                                                        value={formData.first_name}
                                                        onChange={handleInputChange}
                                                    />
                                                    {formErrors.first_name && (
                                                        <div className="invalid-feedback">
                                                            {formErrors.first_name}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="col-md-6 mb-3">
                                                <label className="form-label">Nom</label>
                                                <div className="input-group">
                                                    <span className="input-group-text">
                                                        <User size={18} />
                                                    </span>
                                                    <input
                                                        type="text"
                                                        className={`form-control ${formErrors.last_name ? 'is-invalid' : ''}`}
                                                        name="last_name"
                                                        value={formData.last_name}
                                                        onChange={handleInputChange}
                                                    />
                                                    {formErrors.last_name && (
                                                        <div className="invalid-feedback">
                                                            {formErrors.last_name}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div className="mb-3">
                                        <label className="form-label">E-mail</label>
                                        <div className="input-group">
                                            <span className="input-group-text">
                                                <Mail size={18} />
                                            </span>
                                            <input
                                                type="email"
                                                className={`form-control ${formErrors.email ? 'is-invalid' : ''}`}
                                                name="email"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                            />
                                            {formErrors.email && (
                                                <div className="invalid-feedback">
                                                    {formErrors.email}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">Mot de passe</label>
                                        <div className="input-group">
                                            <span className="input-group-text">
                                                <Lock size={18} />
                                            </span>
                                            <input
                                                type={showPassword ? 'text' : 'password'}
                                                className={`form-control ${formErrors.password ? 'is-invalid' : ''}`}
                                                name="password"
                                                value={formData.password}
                                                onChange={handleInputChange}
                                            />
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary"
                                                onClick={() => setShowPassword(!showPassword)}
                                            >
                                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                            </button>
                                            {formErrors.password && (
                                                <div className="invalid-feedback">
                                                    {formErrors.password}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {!isLogin && (
                                        <div className="mb-3">
                                            <label className="form-label">Confirmez le mot de passe</label>
                                            <div className="input-group">
                                                <span className="input-group-text">
                                                    <Lock size={18} />
                                                </span>
                                                <input
                                                    type={showPassword ? 'text' : 'password'}
                                                    className={`form-control ${formErrors.confirmPassword ? 'is-invalid' : ''}`}
                                                    name="confirmPassword"
                                                    value={formData.confirmPassword}
                                                    onChange={handleInputChange}
                                                />
                                                {formErrors.confirmPassword && (
                                                    <div className="invalid-feedback">
                                                        {formErrors.confirmPassword}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {isLogin && (
                                        <div className="mb-3">
                                            <div className="d-flex justify-content-between align-items-center">
                                                {/* <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="rememberMe"
                          />
                          <label className="form-check-label" htmlFor="rememberMe">
                            Remember me
                          </label>
                        </div> */}
                                                <a href="/forgot-password" className="text-primary text-decoration-none">
                                                    Mot de passe oublié ?
                                                </a>
                                            </div>
                                        </div>
                                    )}

                                    <button
                                        type="submit"
                                        className="btn btn-primary w-100"
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        ) : null}
                                        {isLogin ? 'Connexion' : 'Créer mon compte'}
                                    </button>
                                </form>

                                <div className="text-center mt-4">
                                    <p className="mb-0">
                                        {isLogin ? "Vous n'avez pas encore de compte ?" : "Vous avez déjà un compte ?"}
                                        {' '}
                                        <button
                                            className="btn btn-link p-0"
                                            onClick={() => {
                                                setIsLogin(!isLogin);
                                                setError('');
                                                setFormErrors({});
                                            }}
                                        >
                                            {isLogin ? 'Inscription' : 'Connexion'}
                                        </button>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default AuthPage;